import { Divider, Switch, Text, Toast } from '@byecode/ui'
import type { ApplicationSettingAuthentication } from '@lighthouse/core'
import { ListItem4ByecodeUi, useAtomData } from '@lighthouse/shared'
import { mergeRight } from 'rambda'
import React, { useCallback } from 'react'
import { useSetState } from 'react-use'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { IntegratedCard } from '@/components/IntegratedCard'
import { TagIcon } from '@/components/TagIcon'
import { openOtherPlatform } from '@/containers/OtherPlatforms/OtherPlatformEvent'
import { OtherPlatformFeatureEmpty } from '@/containers/OtherPlatforms/OtherPlatformFeatureEmpty'

import { LoginBlock } from '../LoginBlock'

interface LoginWeChatProps {
    label?: React.ReactNode
    value: ApplicationSettingAuthentication['login']['weChat']
    onChange: (v: ApplicationSettingAuthentication['login']['weChat']) => void
}

interface State {
    mode: 'add' | 'update'
    opened: boolean
    id?: string
    type?: 'website' | 'office'
}

export const LoginWeChat: React.FunctionComponent<LoginWeChatProps> = ({ label, value, onChange }) => {
    const {
        isOpened,
        isOpenedH5QrcodeLogin,
        isBindPhone,
        isOpenedMiniProgram,
        isOpenedMiniProgramProfile,
        isOpenedH5
    } = value ?? {}
    const otherPlatformList = useAtomData(otherPlatformListAtom)

    const [{ opened, mode, id, type }, setState] = useSetState<State>({
        opened: false,
        mode: 'add'
    })

    const wxOfficialAccount = otherPlatformList?.find(v => v.type === 'WECHAT_OFFICIAL_ACCOUNT')
    const isWxOfficialAccount = !!wxOfficialAccount

    const checkWxLoginConfig = useCallback(
        (v: () => void) => {
            const isBind =
                wxOfficialAccount?.type === 'WECHAT_OFFICIAL_ACCOUNT'
                    ? [wxOfficialAccount.config?.token, wxOfficialAccount.config?.encodingAesKey].every(Boolean)
                    : false
            if (isBind) {
                return v()
            }
            Toast.warning('请先完善“微信网页扫码登录”相关配置')
            setState(wxOfficialAccount?.id ? { mode: 'update', id: wxOfficialAccount?.id } : { mode: 'add' })
            if (wxOfficialAccount) {
                openOtherPlatform({
                    type: 'WECHAT_OFFICIAL_ACCOUNT',
                    options: {
                        isPerfectWebsiteInfo: isWxOfficialAccount
                    }
                })
            }
        },
        [isWxOfficialAccount, setState, wxOfficialAccount]
    )

    const handleChange = useCallback(
        (v: Partial<ApplicationSettingAuthentication['login']['weChat']>) => {
            onChange(mergeRight(value, v))
        },
        [onChange, value]
    )

    return (
        <LoginBlock
            label={label}
            checked={isOpened}
            onChange={v => handleChange({ isOpened: v })}
            icon={
                <TagIcon
                    size={32}
                    iconSize={20}
                    radius={100}
                    iconColor="var(--color-green-500)"
                    background="var(--color-green-50)"
                    icon="WeChatLogoIconLibrary"
                />
            }
        >
            <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                <Text>在网页应用中使用微信登录</Text>
                <Switch
                    checked={isOpenedH5 ?? false}
                    onChange={e => {
                        const bool = e.target.checked
                        // @灿白 2024-01-04 17:00:00 不需要
                        // checkWxLoginConfig(() => {
                        handleChange({ isOpenedH5: bool })
                        // })
                    }}
                />
            </ListItem4ByecodeUi>
            {isWxOfficialAccount ? (
                <>
                    {isOpenedH5 && (
                        <>
                            <IntegratedCard type="WECHAT_OFFICIAL_ACCOUNT" />
                            <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                                <Text>开启一键登录（仅支持微信浏览器）</Text>
                                <Switch checked={isWxOfficialAccount} disabled />
                            </ListItem4ByecodeUi>
                            <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                                <Text>开启网页扫码登录（需关注公众号）</Text>
                                <Switch
                                    checked={isOpenedH5QrcodeLogin}
                                    disabled={!isWxOfficialAccount}
                                    onChange={e => {
                                        const bool = e.target.checked
                                        checkWxLoginConfig(() => {
                                            handleChange({ isOpenedH5QrcodeLogin: bool })
                                        })
                                    }}
                                />
                            </ListItem4ByecodeUi>
                        </>
                    )}
                </>
            ) : (
                <OtherPlatformFeatureEmpty type="WECHAT_OFFICIAL_ACCOUNT" />
            )}

            <Divider my={8} />
            <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                <Text>在小程序中使用微信登录</Text>
                <Switch
                    checked={isOpenedMiniProgram ?? false}
                    onChange={e => {
                        const bool = e.target.checked
                        handleChange({
                            isOpenedMiniProgram: bool,
                            ...(bool ? {} : { isOpenedMiniProgramProfile: false })
                        })
                    }}
                />
            </ListItem4ByecodeUi>
            {isOpenedMiniProgram && (
                <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                    <Text>获取头像和昵称</Text>
                    <Switch
                        checked={isOpenedMiniProgramProfile ?? false}
                        onChange={e => {
                            handleChange({ isOpenedMiniProgramProfile: e.target.checked })
                        }}
                    />
                </ListItem4ByecodeUi>
            )}
            <Divider my={8} />
            <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                <Text>微信登录后绑定手机号</Text>
                <Switch
                    checked={isBindPhone}
                    // disabled={!isWxOfficialAccount}
                    onChange={e => {
                        handleChange({ isBindPhone: e.target.checked })
                    }}
                />
            </ListItem4ByecodeUi>
        </LoginBlock>
    )
}
